import React, { useState, useEffect } from 'react';
import { TextField } from '../ui/TextField';
import { Button } from '../ui/button';
import { FeatherUser, FeatherMail, FeatherPhone, FeatherPlus, FeatherRefreshCw } from '../ui/icons';
import api from '../../services/api';

const ContactSelector = ({ partner, onSelect, autoSelect = true, selectedContact = null, actionType = null, className = '' }) => {
  const [contacts, setContacts] = useState([]);
  const [recentContact, setRecentContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    discovered_from: 'other'
  });

  // Note: Contact options are now provided by the API via get_partner_contacts function

  // Get all contacts from API (includes company, primary, decision_maker, and discovered contacts)
  const fetchAllContacts = async () => {
    try {
      const response = await api.get(`/api/partner-companies/${partner.id}/contacts`);
      return response.data.contacts || [];
    } catch (error) {
      console.error('Error fetching contacts:', error);
      // Return empty array on error - this allows the component to still work
      return [];
    }
  };

  // Get recent contact from contact attempts
  const fetchRecentContact = async () => {
    try {
      const response = await api.get(`/api/contact-attempts/partner/${partner.id}/history`);
      const attempts = response.data.history || [];
      if (attempts.length > 0) {
        const lastAttempt = attempts[0];
        return {
          id: lastAttempt.contact_person_id || 'recent',
          name: lastAttempt.contact_person_name,
          email: lastAttempt.contact_person_email,
          phone: lastAttempt.contact_person_phone,
          role: lastAttempt.contact_person_role,
          type: 'recent',
          source: 'contact_attempts',
          displayName: `${lastAttempt.contact_person_name} (Recent)`
        };
      }
    } catch (error) {
      console.error('Error fetching recent contact:', error);
      // Return null on error - this allows the component to still work
    }
    return null;
  };

  // Initialize contacts - optimized to prevent redundant API calls
  useEffect(() => {
    let isMounted = true;
    let isInitializing = false;
    
    const initializeContacts = async () => {
      // Prevent multiple simultaneous calls
      if (loading || isInitializing) {
        console.log('🔄 Skipping initialization - already in progress');
        return;
      }
      
      console.log('🚀 Starting contact initialization for partner:', partner.id);
      isInitializing = true;
      setLoading(true);
      
      try {
        // Get all contacts and recent contact in parallel
        const [allContactsFromAPI, recent] = await Promise.all([
          fetchAllContacts(),
          fetchRecentContact()
        ]);
        
        if (!isMounted) return;
        
        // Use API contacts as the primary source (they include company, primary, decision_maker, and discovered)
        const allContacts = allContactsFromAPI
          .map(contact => {
            // Create proper display names based on contact type
            let displayName;
            switch (contact.type) {
              case 'company':
                displayName = `Company (${contact.email || contact.phone})`;
                break;
              case 'primary':
                displayName = `${contact.name} (Primary)`;
                break;
              case 'decision_maker':
                displayName = `${contact.name} (Decision Maker)`;
                break;
              case 'discovered':
                displayName = `${contact.name} (${contact.role || 'Contact'})`;
                break;
              default:
                displayName = `${contact.name} (${contact.role || 'Contact'})`;
            }
            
            return {
              ...contact,
              displayName
            };
          })
          .filter(contact => {
            // Filter contacts based on action type and required contact info
            if (!actionType) return true; // Show all if no action type specified
            
            switch (actionType) {
              case 'phone':
                // For phone actions, require phone number
                return contact.phone && contact.phone.trim() !== '';
              case 'email':
                // For email actions, require email address
                return contact.email && contact.email.trim() !== '';
              case 'linkedin':
              case 'in_person':
              case 'video_call':
              case 'other':
                // For other actions, require either phone or email
                return (contact.phone && contact.phone.trim() !== '') || 
                       (contact.email && contact.email.trim() !== '');
              default:
                return true;
            }
          });
        
        // Final deduplication by id to ensure no duplicates
        const uniqueContacts = allContacts.filter((contact, index, self) => 
          index === self.findIndex(c => c.id === contact.id)
        );
        
        console.log('📋 Final contacts count:', uniqueContacts.length);
        console.log('🎯 Action type:', actionType);
        console.log('📞 Available contacts:', uniqueContacts.map(c => `${c.name} (${c.type}) - Phone: ${c.phone ? 'Yes' : 'No'}, Email: ${c.email ? 'Yes' : 'No'}`));
        setContacts(uniqueContacts);
        setRecentContact(recent);
        
        // Auto-select if enabled
        if (autoSelect && !selectedContact) {
          // Prioritize recent contact if it matches action type requirements
          let contactToSelect = null;
          
          if (recent && allContacts.some(c => c.id === recent.id)) {
            contactToSelect = recent;
          } else if (allContacts.length > 0) {
            // Select first available contact that matches action type
            contactToSelect = allContacts[0];
          }
          
          if (contactToSelect) {
            onSelect(contactToSelect);
          }
        }
      } catch (error) {
        console.error('Error initializing contacts:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
          isInitializing = false;
        }
      }
    };

    initializeContacts();
    
    return () => {
      isMounted = false;
    };
  }, [partner.id]); // Removed autoSelect and selectedContact from dependencies

  // Handle contact selection
  const handleContactSelect = (contactId) => {
    if (contactId === 'add_new') {
      setShowAddForm(true);
      return;
    }
    
    const selected = contacts.find(c => c.id === contactId);
    if (selected) {
      onSelect(selected);
    }
  };

  // Handle adding new contact
  const handleAddContact = async () => {
    if (!newContact.name.trim()) return;
    
    try {
      const response = await api.post(`/api/partner-companies/${partner.id}/contacts`, newContact);
      const addedContact = response.data.contact;
      
      // Add to contacts list
      const contactWithDisplay = {
        ...addedContact,
        type: 'discovered',
        source: 'contact_persons',
        displayName: `${addedContact.name} (${addedContact.role || 'Contact'})`
      };
      
      setContacts(prev => [...prev, contactWithDisplay]);
      
      // Select the new contact
      onSelect(contactWithDisplay);
      
      // Reset form
      setNewContact({ name: '', role: '', email: '', phone: '', discovered_from: 'other' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding contact:', error);
    }
  };

  // Show add contact form
  if (showAddForm) {
    return (
      <div className={`one-click-contact-selector ${className}`}>
        <div className="one-click-contact-add-form">
          <div className="one-click-contact-add-header">
            <FeatherPlus size={16} />
            <h5>Add New Contact</h5>
          </div>
          
          <div className="one-click-contact-add-body">
            <TextField label="Name" className="one-click-text-field">
              <TextField.Input
                value={newContact.name}
                onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Contact name"
                autoFocus
                className="one-click-text-field-input"
              />
            </TextField>
            
            <TextField label="Role" className="one-click-text-field">
              <TextField.Input
                value={newContact.role}
                onChange={(e) => setNewContact(prev => ({ ...prev, role: e.target.value }))}
                placeholder="Job title"
                className="one-click-text-field-input"
              />
            </TextField>
          </div>
          
          <TextField label="Email" className="one-click-text-field">
            <TextField.Input
              type="email"
              value={newContact.email}
              onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
              placeholder="contact@company.com"
              className="one-click-text-field-input"
            />
          </TextField>
          
          <TextField label="Phone" className="one-click-text-field">
            <TextField.Input
              value={newContact.phone}
              onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="+1-555-0123"
              className="one-click-text-field-input"
            />
          </TextField>
          
          <div className="one-click-contact-add-actions">
            <Button
              variant="neutral-tertiary"
              onClick={() => setShowAddForm(false)}
              className="one-click-button"
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              onClick={handleAddContact}
              disabled={!newContact.name.trim()}
              className="one-click-button"
            >
              Add Contact
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes oneClickContactSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className={`one-click-contact-selector ${className}`}>
      <div className="one-click-text-field">
        <label className="one-click-text-field-label">Contact Person</label>
        {loading ? (
          <div className="contact-loading" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            backgroundColor: '#f8fafc',
            color: '#64748b',
            fontSize: '14px'
          }}>
            <FeatherRefreshCw size={16} style={{
              animation: 'oneClickContactSpin 1s linear infinite'
            }} />
            <span>Loading contacts...</span>
          </div>
        ) : contacts.length === 0 && actionType ? (
          <div className="add-contact-required" style={{
            padding: '16px',
            border: '2px solid #f59e0b',
            borderRadius: '8px',
            backgroundColor: '#fefbf0',
            fontSize: '14px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#f59e0b',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '8px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                !
              </div>
              <p style={{ margin: 0, fontWeight: '600', color: '#92400e' }}>
                Contact Required
              </p>
            </div>
            <p style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '13px' }}>
              {actionType === 'phone' && 'No contacts with phone numbers found. Please add a contact with a phone number to proceed.'}
              {actionType === 'email' && 'No contacts with email addresses found. Please add a contact with an email address to proceed.'}
              {['linkedin', 'in_person', 'video_call', 'other'].includes(actionType) && 'No contacts with phone or email found. Please add a contact with the required information to proceed.'}
            </p>
            <button
              onClick={() => handleContactSelect('add_new')}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              ➕ Add New Contact
            </button>
          </div>
        ) : (
          <select
            className="one-click-contact-select-dropdown"
            value={selectedContact?.id || ''}
            onChange={(e) => handleContactSelect(e.target.value)}
            disabled={loading}
          >
            <option value="">Select a contact...</option>
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.displayName}
              </option>
            ))}
            <option value="add_new">➕ Add New Contact...</option>
          </select>
        )}
      </div>
      
      {selectedContact && (
        <div className="selected-contact-info">
          <div className="contact-details">
            <div className="contact-name">
              <FeatherUser size={16} />
              {selectedContact.name}
            </div>
            {selectedContact.role && (
              <div className="contact-role">{selectedContact.role}</div>
            )}
            {selectedContact.email && (
              <div className="contact-email">
                <FeatherMail size={14} />
                {selectedContact.email}
              </div>
            )}
            {selectedContact.phone && (
              <div className="contact-phone">
                <FeatherPhone size={14} />
                {selectedContact.phone}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ContactSelector; 